/**
 * ═══════════════════════════════════════════════════════════════
 * FEEDBACK BUTTON - Planivio v3.5
 * ═══════════════════════════════════════════════════════════════
 *
 * ✅ v3.5: TRANSPARENT + DRAGGABLE
 *          - Button ist jetzt halbtransparent
 *          - Kann per Drag&Drop verschoben werden
 *          - Position wird in localStorage gespeichert
 * ✅ v3.4: Position links unten
 * ✅ v3.2: POSITION FIX - überdeckt Footer-Buttons nicht mehr
 * ✅ v3.1: Position über Fixed Footer
 * ✅ v3.0: Planivio Rebranding
 *
 * @version 3.5.0 - Transparent + Draggable
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';

// ═══════════════════════════════════════════════════════════════
// KONFIGURATION
// ═══════════════════════════════════════════════════════════════

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const FEEDBACK_URL = `${API_BASE_URL}/api/feedback`;

const DEBUG = false;  // ✅ Deaktiviert - kein "T" Badge mehr sichtbar

// ✅ v3.8: LocalStorage Key für Position (v4 = Reset auf links)
const POSITION_STORAGE_KEY = 'planivio_feedback_button_position_v4';

// ✅ v3.8: Default Position - LINKS UNTEN
const getDefaultPosition = () => ({
  x: 18,                       // Linke Seite (18px Margin)
  y: window.innerHeight - 140  // Unten, über Footer-Buttons
});

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

const FeedbackButton = () => {
  const { isAuthenticated, getIdTokenClaims, getAccessToken } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [n8nMode, setN8nMode] = useState('production');

  // ✅ v3.1: Hover State für Label
  const [isHovered, setIsHovered] = useState(false);

  // ✅ v3.5: Drag State
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem(POSITION_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validieren dass Position im Viewport ist
        return {
          x: Math.min(Math.max(0, parsed.x), window.innerWidth - 60),
          y: Math.min(Math.max(0, parsed.y), window.innerHeight - 60)
        };
      }
    } catch (e) {
      console.warn('Fehler beim Laden der Feedback-Button Position');
    }
    return getDefaultPosition();
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false); // ✅ Unterscheidet Click von Drag
  const buttonRef = useRef(null);

  // ✅ v3.5: Position speichern
  const savePosition = useCallback((pos) => {
    try {
      localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(pos));
    } catch (e) {
      console.warn('Fehler beim Speichern der Feedback-Button Position');
    }
  }, []);

  // ✅ v3.5: Drag Start (Mouse + Touch)
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;

    setIsDragging(true);
    setHasDragged(false); // Reset bei neuem Drag
    setDragOffset({
      x: clientX - position.x,
      y: clientY - position.y
    });
  }, [position]);

  // ✅ v3.5: Drag Move
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;

    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;

    const newX = Math.min(Math.max(0, clientX - dragOffset.x), window.innerWidth - 60);
    const newY = Math.min(Math.max(0, clientY - dragOffset.y), window.innerHeight - 60);

    // Nur als "gedraggt" markieren wenn wirklich bewegt wurde
    if (Math.abs(newX - position.x) > 5 || Math.abs(newY - position.y) > 5) {
      setHasDragged(true);
    }

    setPosition({ x: newX, y: newY });
  }, [isDragging, dragOffset, position]);

  // ✅ v3.5: Drag End
  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      savePosition(position);
    }
  }, [isDragging, position, savePosition]);

  // ✅ v3.5: Event Listeners für Drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);

      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);
  
  const [formData, setFormData] = useState({
    category: 'general',
    rating: 0,
    title: '',
    message: '',
    email: ''
  });

  const categories = [
    { value: 'bug', label: '🐛 Bug melden', color: '#EF4444' },
    { value: 'feature', label: '💡 Feature Wunsch', color: '#8B5CF6' },
    { value: 'ux', label: '🎨 User Experience', color: '#3B82F6' },
    { value: 'general', label: '💬 Allgemein', color: '#22C55E' }
  ];

  // Email vorausfüllen wenn Modal geöffnet und eingeloggt
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      getIdTokenClaims().then(claims => {
        const email = claims?.email;
        if (email) setFormData(prev => ({ ...prev, email }));
      }).catch(() => {});
    }
  }, [isOpen, isAuthenticated, getIdTokenClaims]);

  // ═══════════════════════════════════════════════════════════
  // SEND DIRECT TO N8N
  // ═══════════════════════════════════════════════════════════

  const sendToN8N = async (payload, accessToken) => {
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

    const response = await fetch(FEEDBACK_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      throw new Error(`N8N error: ${response.status}`);
    }
    
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      return { success: true, raw: text };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      let userId = 'anonymous';
      let userEmail = 'nicht eingeloggt';
      let accessToken = null;

      if (isAuthenticated) {
        try {
          const claims = await getIdTokenClaims();
          userId = claims?.sub || 'anonymous';
          userEmail = claims?.email || formData.email || 'keine Email';
          accessToken = await getAccessToken(process.env.REACT_APP_LOGTO_API_RESOURCE);
        } catch (claimsError) {
          console.warn('⚠️ Could not get user claims/token');
        }
      } else {
        userEmail = formData.email || 'nicht eingeloggt';
      }

      const payload = {
        category: formData.category,
        categoryLabel: categories.find(c => c.value === formData.category)?.label || formData.category,
        rating: formData.rating,
        ratingStars: '⭐'.repeat(formData.rating) || 'Keine Bewertung',
        title: formData.title || `${categories.find(c => c.value === formData.category)?.label}`,
        message: formData.message,
        userId,
        userEmail,
        isAuthenticated,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString(),
        timestampLocal: new Date().toLocaleString('de-DE'),
        source: 'Planivio-App',
        environment: n8nMode,
        n8nMode
      };
      
      const result = await sendToN8N(payload, accessToken);
      console.log('✅ Feedback Result:', result);
      
      setSuccessMessage(result.message || 'Feedback received');
      setSubmitted(true);
      
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setSuccessMessage(null);
        setFormData({ category: 'general', rating: 0, title: '', message: '', email: '' });
      }, 3000);
      
    } catch (err) {
      console.error('❌ Feedback Fehler:', err);
      setError(`Fehler: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRating = (value) => {
    setFormData(prev => ({ ...prev, rating: value }));
  };

  // ═══════════════════════════════════════════════════════════
  // ✅ v3.5: STYLES MIT TRANSPARENZ + DRAG
  // ═══════════════════════════════════════════════════════════
  const styles = {
    // ✅ v3.5: Position per Drag - transparent
    container: {
      position: 'fixed',
      left: `${position.x}px`,
      top: `${position.y}px`,
      display: 'flex',
      alignItems: 'center',
      gap: '0',
      zIndex: 50,
      pointerEvents: 'auto',
      opacity: isDragging ? 0.9 : 0.6, // ✅ Transparent
      transition: isDragging ? 'none' : 'opacity 0.3s ease',
      cursor: isDragging ? 'grabbing' : 'grab',
      userSelect: 'none',
      touchAction: 'none'
    },

    // ✅ v3.5: Label erscheint bei Hover - rechts vom Button
    feedbackLabel: {
      background: 'linear-gradient(135deg, rgba(74, 144, 164, 0.9) 0%, rgba(107, 168, 147, 0.9) 100%)',
      color: 'white',
      padding: '10px 16px',
      borderRadius: '0 12px 12px 0',
      fontSize: '0.9rem',
      fontWeight: 600,
      boxShadow: '0 4px 15px rgba(74, 144, 164, 0.2)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      opacity: isHovered && !isDragging ? 1 : 0,
      transform: isHovered && !isDragging ? 'translateX(0)' : 'translateX(-20px)',
      pointerEvents: isHovered && !isDragging ? 'auto' : 'none',
      whiteSpace: 'nowrap',
      order: 2
    },

    // ✅ v3.5: Button transparent + draggable
    floatingButton: {
      width: '52px',
      height: '52px',
      borderRadius: isHovered && !isDragging ? '12px 0 0 12px' : '12px',
      order: 1,
      background: 'linear-gradient(135deg, rgba(74, 144, 164, 0.85) 0%, rgba(107, 168, 147, 0.85) 100%)',
      border: '2px solid rgba(255, 255, 255, 0.3)',
      cursor: isDragging ? 'grabbing' : 'grab',
      boxShadow: isDragging
        ? '0 8px 30px rgba(74, 144, 164, 0.5)'
        : '0 4px 15px rgba(74, 144, 164, 0.25)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.4rem',
      transition: isDragging ? 'none' : 'all 0.3s ease',
      position: 'relative',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)'
    },
    
    modeBadge: {
      position: 'absolute',
      top: '-6px',
      right: '-6px',
      background: '#22C55E',
      color: 'white',
      fontSize: '0.5rem',
      fontWeight: 'bold',
      padding: '2px 4px',
      borderRadius: '6px',
      textTransform: 'uppercase'
    },
    
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem'
    },
    
    modal: {
      background: 'white',
      borderRadius: '20px',
      width: '100%',
      maxWidth: '480px',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    
    header: {
      background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
      padding: '1.25rem 1.5rem',
      borderRadius: '20px 20px 0 0',
      color: 'white',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    
    headerTitle: {
      margin: 0,
      fontSize: '1.25rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    
    headerBadge: {
      fontSize: '0.7rem',
      background: 'rgba(255,255,255,0.25)',
      padding: '4px 10px',
      borderRadius: '12px'
    },
    
    closeButton: {
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      borderRadius: '50%',
      width: '32px',
      height: '32px',
      cursor: 'pointer',
      fontSize: '1.25rem',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    
    body: {
      padding: '1.5rem'
    },
    
    miniToggleContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '8px',
      marginBottom: '1rem',
      opacity: 0.7
    },
    
    miniToggleLabel: {
      fontSize: '0.75rem',
      color: '#6B7280',
      fontWeight: 500
    },
    
    miniToggle: {
      width: '44px',
      height: '24px',
      borderRadius: '12px',
      border: 'none',
      cursor: 'pointer',
      position: 'relative',
      transition: 'background 0.3s ease',
      padding: 0
    },
    
    miniToggleKnob: {
      position: 'absolute',
      top: '2px',
      left: '2px',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      background: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      transition: 'transform 0.3s ease'
    },
    
    formLabel: {
      display: 'block',
      fontSize: '0.9rem',
      fontWeight: 600,
      color: '#374151',
      marginBottom: '0.5rem'
    },
    
    categoryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '0.5rem',
      marginBottom: '1.25rem'
    },
    
    categoryButton: {
      padding: '0.75rem',
      border: '2px solid #E5E7EB',
      borderRadius: '12px',
      background: 'white',
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: 600,
      transition: 'all 0.2s',
      textAlign: 'center'
    },
    
    ratingContainer: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1.25rem'
    },
    
    starButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.75rem',
      cursor: 'pointer',
      transition: 'transform 0.2s'
    },
    
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #E5E7EB',
      borderRadius: '12px',
      fontSize: '1rem',
      marginBottom: '1rem',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box'
    },
    
    textarea: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #E5E7EB',
      borderRadius: '12px',
      fontSize: '1rem',
      marginBottom: '1.25rem',
      outline: 'none',
      resize: 'vertical',
      minHeight: '100px',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    
    submitButton: {
      width: '100%',
      padding: '1rem',
      background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: '0 4px 15px rgba(74, 144, 164, 0.3)'
    },
    
    successContainer: {
      textAlign: 'center',
      padding: '2rem'
    },
    
    successIcon: {
      fontSize: '4rem',
      marginBottom: '1rem'
    },
    
    successTitle: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#22C55E',
      marginBottom: '0.5rem'
    },
    
    successText: {
      color: '#6B7280'
    },
    
    n8nResponse: {
      marginTop: '0.75rem',
      padding: '0.5rem 1rem',
      background: '#ECFDF5',
      color: '#059669',
      borderRadius: '8px',
      fontSize: '0.85rem',
      fontWeight: 500
    },
    
    errorText: {
      color: '#EF4444',
      fontSize: '0.875rem',
      marginBottom: '1rem',
      padding: '0.75rem',
      background: '#FEF2F2',
      borderRadius: '8px'
    }
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <>
      {/* ✅ v3.5: Container mit Drag + Hover-Effekt */}
      <div
        ref={buttonRef}
        style={styles.container}
        onMouseEnter={() => !isDragging && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        {/* Label erscheint bei Hover */}
        <span
          style={styles.feedbackLabel}
          onClick={(e) => {
            e.stopPropagation();
            // ✅ v3.5: Nur öffnen wenn NICHT gedraggt wurde
            if (!hasDragged) setIsOpen(true);
          }}
        >
          Feedback
        </span>

        {/* Floating Button - transparent + draggable */}
        <button
          style={{
            ...styles.floatingButton,
            transform: isHovered && !isDragging ? 'scale(1.05)' : 'scale(1)'
          }}
          onClick={(e) => {
            e.stopPropagation();
            // ✅ v3.5: Nur öffnen wenn NICHT gedraggt wurde (Schwellenwert 5px)
            if (!hasDragged) setIsOpen(true);
          }}
          title="Feedback geben (ziehen zum Verschieben)"
        >
          💬
          {DEBUG && (
            <span style={styles.modeBadge}>
              {'L'}
            </span>
          )}
        </button>
      </div>

      {/* Modal */}
      {isOpen && (
        <div style={styles.overlay} onClick={() => !isSubmitting && setIsOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div style={styles.header}>
              <h2 style={styles.headerTitle}>
                💬 Feedback
                <span style={styles.headerBadge}>
                  {n8nMode === 'production' ? '🚀 Live' : '🧪 Test'}
                </span>
              </h2>
              <button 
                style={styles.closeButton}
                onClick={() => setIsOpen(false)}
                disabled={isSubmitting}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={styles.body}>
              {submitted ? (
                <div style={styles.successContainer}>
                  <div style={styles.successIcon}>🎉</div>
                  <h3 style={styles.successTitle}>Vielen Dank!</h3>
                  <p style={styles.successText}>
                    Dein Feedback wurde gesendet.
                  </p>
                  {successMessage && (
                    <p style={styles.n8nResponse}>
                      ✅ {successMessage}
                    </p>
                  )}
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  
                  {/* Toggle: Test/Production - auskommentiert für Produktion
                  <div style={styles.miniToggleContainer}>
                    <span style={styles.miniToggleLabel}>
                      {n8nMode === 'production' ? '🚀 Live' : '🧪 Test'}
                    </span>
                    <button
                      type="button"
                      onClick={() => setN8nMode(m => m === 'production' ? 'test' : 'production')}
                      style={{
                        ...styles.miniToggle,
                        background: n8nMode === 'production' ? '#22C55E' : '#F59E0B'
                      }}
                    >
                      <span style={{
                        ...styles.miniToggleKnob,
                        transform: n8nMode === 'production' ? 'translateX(20px)' : 'translateX(0)'
                      }} />
                    </button>
                  </div>
                  */}
                  
                  {/* Error */}
                  {error && <div style={styles.errorText}>{error}</div>}
                  
                  {/* Kategorie */}
                  <label style={styles.formLabel}>Kategorie</label>
                  <div style={styles.categoryGrid}>
                    {categories.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        style={{
                          ...styles.categoryButton,
                          borderColor: formData.category === cat.value ? cat.color : '#E5E7EB',
                          background: formData.category === cat.value ? `${cat.color}15` : 'white',
                          color: formData.category === cat.value ? cat.color : '#374151'
                        }}
                        onClick={() => setFormData(prev => ({ ...prev, category: cat.value }))}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>

                  {/* Rating */}
                  <label style={styles.formLabel}>Bewertung (optional)</label>
                  <div style={styles.ratingContainer}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        style={{
                          ...styles.starButton,
                          transform: formData.rating >= star ? 'scale(1.2)' : 'scale(1)'
                        }}
                        onClick={() => handleRating(star)}
                      >
                        {formData.rating >= star ? '⭐' : '☆'}
                      </button>
                    ))}
                  </div>

                  {/* E-Mail */}
                  <label style={styles.formLabel}>E-Mail (optional)</label>
                  <input
                    type="email"
                    style={styles.input}
                    placeholder="deine@email.de"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    onFocus={(e) => e.target.style.borderColor = '#4A90A4'}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  />

                  {/* Titel */}
                  <label style={styles.formLabel}>Betreff (optional)</label>
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="Kurze Zusammenfassung..."
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    onFocus={(e) => e.target.style.borderColor = '#4A90A4'}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  />

                  {/* Nachricht */}
                  <label style={styles.formLabel}>Deine Nachricht *</label>
                  <textarea
                    style={styles.textarea}
                    placeholder="Beschreibe dein Feedback..."
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    onFocus={(e) => e.target.style.borderColor = '#4A90A4'}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                    required
                  />

                  {/* Submit */}
                  <button
                    type="submit"
                    style={{
                      ...styles.submitButton,
                      opacity: isSubmitting ? 0.7 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer'
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '⏳ Wird gesendet...' : '📤 Feedback senden'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;

